import { PlusIcon } from '@heroicons/react/solid';
import {
  FormField,
  WajeezFormInput,
} from '@wajeez-web/components/form-builder';
import {
  Button,
  InputLabel,
  LanguagesLayout,
  Spin,
} from '@wajeez-web/components/ui';
import { Server } from '@wajeez-web/core/api-base';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import { useContentSelector } from '@wajeez-web/stores/content';
import {
  AlertType,
  CommonActions,
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import {
  FieldValues,
  UseControllerProps,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Languages } from '@wajeez-web/core/utils';

import ContentEntityNarratorAudio from './ContentEntityNarratorAudio';
import ContentEntityTexts from './ContentEntityTexts';

export const baseFields = {
  title: {
    fieldType: Server.FieldType.Text,
    fieldOptions: {},
    placeHolder: 'ادخل العنوان',
    label: 'العنوان',
    required: true,
  },
  originalTitle: {
    fieldType: Server.FieldType.Text,
    fieldOptions: {},
    placeHolder: 'ادخل العنوان باللغة الانجليزية',
    label: 'العنوان باللغة الانجليزية',
    required: true,
  },
  originalSubTitle: {
    fieldType: Server.FieldType.Text,
    fieldOptions: {},
    placeHolder: 'ادخل العنوان الفرعي باللغة الانجليزية',
    label: 'العنوان الفرعي باللغة الانجليزية',
    required: false,
  },
  actualCost: {
    fieldType: Server.FieldType.Number,
    fieldOptions: {
      min: 0,
    },
    placeHolder: 'ادخل القيمة الحقيقية',
    label: 'القيمة الحقيقية',
    required: false,
  },
  actualDurationMS: {
    fieldType: Server.FieldType.Number,
    fieldOptions: {
      min: 0,
    },
    placeHolder: 'ادخل المدة الحقيقية',
    label: 'المدة الحقيقية',
    required: false,
  },
  publishDate: {
    fieldType: Server.FieldType.Date,
    fieldOptions: {},
    placeHolder: 'ادخل تاريخ النشر',
    label: 'تاريخ النشر',
    required: false,
  },
  slug: {
    fieldType: Server.FieldType.Text,
    fieldOptions: {},
    placeHolder: 'ادخل الرمز التعريفي',
    label: 'الرمز التعريفي',
    required: false,
  },
};

export interface CreateContentEntityBasicInfoLoaderProps {
  id: string | undefined;
  type: Server.ContentEntityTypeDetailedOutput;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  target?: 'page' | 'modal';
  create?: boolean;
  item?: boolean;
}

const CreateContentEntityBasicInfoLoader = ({
  id,
  type,
  onSubmit,
}: CreateContentEntityBasicInfoLoaderProps) => {
  const navigate = useNavigate();

  const dispatch = useCommonDispatch();

  const { data, status } = useContentSelector(
    (s) => s.contentEntity.detailsByIdMultilingual
  );
  const createMultilingual = useContentSelector(
    ({ contentEntity }) => contentEntity.createMultilingual
  );
  const editMultilingual = useContentSelector(
    ({ contentEntity }) => contentEntity.editMultilingual
  );
  const assignStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.assignStatus
  );

  const loading = status === 'loading';

  const sortedFields =
    type.fields?.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [];

  if (loading)
    return (
      <div className="min-h-[300px] flex flex-col justify-center items-center">
        <Spin />
      </div>
    );

  return (
    <CreateContentEntityBasicInfo
      onSubmit={onSubmit}
      type={type}
      onCancel={() => {
        navigate(-1);
      }}
      target="page"
      isUpdating={
        createMultilingual.status === 'loading' ||
        editMultilingual.status === 'loading' ||
        assignStatus === 'loading'
      }
      details={
        data != undefined
          ? {
              contentEntityTypeId: type.id ?? 0,
              originalSubTitle: data?.originalSubTitle,
              originalTitle: data?.originalTitle,
              actualCost: data?.actualCost,
              actualDurationMS: data?.actualDurationMS,
              slug: data.slug,
              profiles:
                data?.profiles?.map((p) => {
                  return {
                    ...p,
                    languageId: p.languageId ?? 0,
                    publishDate: p.publishDate,
                    title: p.title as string,
                    fields:
                      sortedFields?.map((f) => {
                        return {
                          ...f,
                          name: f.name as string,
                          value: p?.parameters?.find((p) => p.key === f.name)
                            ?.value,
                        };
                      }) ?? [],
                  };
                }) ?? [],
            }
          : undefined
      }
    />
  );
};

export interface CreateContentEntityBasicInfoProps {
  type: Server.ContentEntityTypeDetailedOutput;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  target?: 'page' | 'modal';
  details?: Partial<Server.CreateContentEntityByTypeIdV2Input>;
  isUpdating?: boolean;
}

export const CreateContentEntityBasicInfo = ({
  type,
  onSubmit,
  onCancel,
  details,
  target = 'modal',
  isUpdating,
}: CreateContentEntityBasicInfoProps) => {
  const commonDispatch = useCommonDispatch();

  const languageId = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const sortedFields =
    type.fields?.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [];

  const navigate = useNavigate();

  const permissions = useAuthSelector((s) => s.auth.permission ?? []);

  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    register,
    formState: { errors },
    watch,
    getValues,
  } = useForm<Server.CreateContentEntityByTypeIdV2Input>({
    defaultValues: details
      ? {
          ...details,
          profiles: details?.profiles?.map((s) => ({
            ...s,
            narratorAudios:
              (s.narratorAudios?.length ?? 0) > 0
                ? s.narratorAudios
                : type.requireAudio
                ? [
                    {
                      narratorId: 0,
                      isDefault: true,
                    },
                  ]
                : [],
            texts:
              (s.texts?.length ?? 0) > 0
                ? s.texts
                : type.requireText
                ? [
                    {
                      isDefault: true,
                    },
                  ]
                : [],
          })) ?? [
            {
              languageId: Languages.AR,
              chapters: [],
              narratorAudios: type.requireAudio
                ? [
                    {
                      narratorId: 0,
                      isDefault: true,
                    },
                  ]
                : [],
              texts: type.requireText
                ? [
                    {
                      isDefault: true,
                    },
                  ]
                : [],
              fields: sortedFields.map((f) => {
                return {
                  name: f.name,
                  value: undefined,
                };
              }),
            },
          ],
        }
      : {
          contentEntityTypeId: type.id,
          profiles: [
            {
              languageId: Languages.AR,
              chapters: [],
              narratorAudios: type.requireAudio
                ? [
                    {
                      narratorId: 0,
                      isDefault: true,
                    },
                  ]
                : [],
              texts: type.requireText
                ? [
                    {
                      isDefault: true,
                    },
                  ]
                : [],
              fields: sortedFields.map((f) => {
                return {
                  name: f.name,
                  value: undefined,
                };
              }),
            },
          ],
        },
  });

  // watch(console.log);

  const {
    fields: profiles,
    append: appendProfile,
    remove: removeProfile,
  } = useFieldArray<
    Server.CreateContentEntityByTypeIdV2Input,
    'profiles',
    'languageId'
  >({
    control,
    name: 'profiles',
  });

  const renderProfile = (language: Languages) => {
    const profiles = getValues('profiles');

    const currentProfile = profiles.find((p) => p.languageId === language);

    const selectedResponseProfileIndex = profiles.findIndex(
      (p) => p.languageId === language
    );

    const isRtl =
      (currentProfile?.languageId as number | undefined)?.toString() ==
      Languages.AR.toString();

    return (
      <div
        key={`profiles.${currentProfile?.languageId}.title`}
        className="w-full"
      >
        {type.title && (
          <div className={`md:col-span-${3} col-span-${3} `}>
            <WajeezFormInput
              control={control}
              name={`profiles.${selectedResponseProfileIndex}.title`}
              type={baseFields['title'] as FormField}
              isReadOnly={false}
              setError={setError}
              isRtl={isRtl}
            />
          </div>
        )}
        {type.requirePublishDate && (
          <div className={`md:col-span-${3} col-span-${3} `}>
            <WajeezFormInput
              control={control}
              name={`profiles.${selectedResponseProfileIndex}.publishDate`}
              type={baseFields['publishDate'] as FormField}
              isReadOnly={!!details}
              setError={setError}
            />
          </div>
        )}
        <div className="mt-8 h-[1px] w-full"></div>
        <div className={`pt-4 border-t border-warmGray-400`}>
          {currentProfile?.fields?.map((field, i) => {
            const name =
              `profiles.${selectedResponseProfileIndex}.fields.${i}.value` as const;
            const item = sortedFields[i];

            return (
              <div key={name} tabIndex={i + 10}>
                {item ? (
                  <WajeezFormInput
                    control={control}
                    name={name}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    type={{
                      ...item,
                      // This was from the requirements so the validation errors appear just on
                      // changing the publish state.
                      required: false,
                    }}
                    isReadOnly={
                      item.permission && !permissions.includes(item.permission)
                        ? true
                        : false
                    }
                    isRtl={isRtl}
                    setError={setError}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        {type.requireText && (
          <ProfileTextsFields
            contentEntityType={type}
            name={`profiles.${selectedResponseProfileIndex}.texts`}
            profileIndex={selectedResponseProfileIndex}
            texts={currentProfile?.texts}
            control={control}
            isRtl={isRtl}
          />
        )}
        {type.requireAudio && (
          <ProfileNarratorAudiosFields
            contentEntityType={type}
            name={`profiles.${selectedResponseProfileIndex}.narratorAudios`}
            profileIndex={selectedResponseProfileIndex}
            narratorAudios={currentProfile?.narratorAudios}
            control={control}
            setValue={setValue}
            register={register}
          />
        )}
      </div>
    );
  };

  const renderFields = () => {
    return (
      <div className={`${target !== 'modal' && 'pt-8'} space-y-8`}>
        <div>
          <div className="space-y-6">
            <div className={`w-full`}>
              <div className="px-4">
                {type.requireOriginalTitle && (
                  <div className={`md:col-span-${3} col-span-${3} `}>
                    <WajeezFormInput
                      control={control}
                      name={'originalTitle'}
                      type={baseFields['originalTitle'] as FormField}
                      isReadOnly={false}
                      setError={setError}
                    />
                  </div>
                )}
                {type.requireOriginalSubTitle && (
                  <div className={`md:col-span-${3} col-span-${3} `}>
                    <WajeezFormInput
                      control={control}
                      name={'originalSubTitle'}
                      type={baseFields['originalSubTitle'] as FormField}
                      isReadOnly={false}
                      setError={setError}
                    />
                  </div>
                )}
                {type.requireActualCost && (
                  <div className={`md:col-span-${3} col-span-${3} `}>
                    <WajeezFormInput
                      control={control}
                      name={'actualCost'}
                      type={baseFields['actualCost'] as FormField}
                      isReadOnly={false}
                      setError={setError}
                    />
                  </div>
                )}
                {type.requireActualDurationMS && (
                  <div className={`md:col-span-${3} col-span-${3} `}>
                    <WajeezFormInput
                      control={control}
                      name={'actualDurationMS'}
                      type={baseFields['actualDurationMS'] as FormField}
                      isReadOnly={false}
                      setError={setError}
                    />
                  </div>
                )}
                <div className={`md:col-span-${3} col-span-${3} `}>
                  <WajeezFormInput
                    control={control}
                    name={'slug'}
                    type={baseFields['slug'] as FormField}
                    isReadOnly={false}
                    setError={setError}
                  />
                </div>
              </div>
              <div className="">
                <LanguagesLayout
                  renderPage={renderProfile}
                  defaultLanguages={profiles.map((s) => s.languageId)}
                  onAdd={function (language: Languages): void {
                    const currentProfile = profiles?.[0];

                    appendProfile({
                      languageId: language,
                      publishDate: undefined,
                      title: '',
                      fields:
                        currentProfile?.fields ??
                        sortedFields.map((f) => {
                          return {
                            name: f.name as string,
                            value: null,
                          };
                        }),
                      narratorAudios: type.requireAudio
                        ? [
                            {
                              narratorId: 0,
                              isDefault: true,
                            },
                          ]
                        : [],
                      texts: type.requireText
                        ? [
                            {
                              isDefault: true,
                            },
                          ]
                        : [],
                    });
                  }}
                  onDelete={function (language: Languages): void {
                    const selectedResponseProfileIndex = profiles.findIndex(
                      (p) => p.languageId === language
                    );
                    removeProfile(selectedResponseProfileIndex);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full">
      <div
        className={`absolute ${
          target === 'modal'
            ? 'top-[100%] rtl:left-0 ltr:right-0 py-8'
            : 'top-0 rtl:left-4 ltr:right-4'
        } flex justify-end space-s-2 rounded-md`}
      >
        <Button
          // refName={"create-book"}
          title="Cancel"
          disabled={isUpdating}
          onClick={() => {
            Swal.fire({
              title: t(`general.${'Are you sure?'}`, '') as string,
              text: t(
                `general.${"You won't be able to revert this!"}`,
                ''
              ) as string,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: t(`general.${'Yes'}`, '') as string,
              cancelButtonText: t(`general.${'Cancel'}`, '') as string,
            }).then((result) => {
              if (result.isConfirmed) {
                if (onCancel) {
                  onCancel();
                } else {
                  navigate(-1);
                }
              }
            });
          }}
          bg="transparent"
          type="ghost"
          color="textPrimary"
        />
        <Button
          // refName={"create-book"}
          loading={isUpdating}
          title="save"
          onClick={async () => {
            let isError = false;

            if (JSON.stringify(errors) !== '{}') {
              for (const key in errors) {
                const typedKey = key as keyof typeof errors;

                const stringifiedError = JSON.stringify(errors[typedKey]);

                if (
                  errors[typedKey] &&
                  stringifiedError !== '[]' &&
                  stringifiedError !== '{}'
                ) {
                  isError = true;

                  break;
                }
              }
            }

            if (!isError) {
              handleSubmit(onSubmit, (e) => console.log(e))();
            } else {
              commonDispatch(
                CommonActions.setAlert({
                  for: 'page',
                  show: true,
                  type: AlertType.ERROR,
                  messages: [
                    {
                      code: 400,
                      field: null,
                      message: t(
                        `general.${'Enter the required fields!'}`,
                        'Enter the required fields!'
                      ),
                    },
                  ],
                })
              );
            }
          }}
          bg="primary"
          color="white"
        />
      </div>
      {renderFields()}
    </div>
  );
};

export default CreateContentEntityBasicInfoLoader;

interface ProfileTextFieldsProps<FormFields extends FieldValues>
  extends UseControllerProps<FormFields> {
  profileIndex: number;
  contentEntityType: Server.ContentEntityTypeDetailedOutput;
  texts: Server.TextInput[] | undefined;
  isRtl?: boolean;
}

export const ProfileTextsFields = ({
  control,
  profileIndex,
  contentEntityType,
  texts,
  isRtl,
}: ProfileTextFieldsProps<Server.CreateContentEntityByTypeIdV2Input>) => {
  const contentEntityTypes = useContentSelector(
    ({ contentEntityType }) => contentEntityType.data ?? []
  );

  const contentEntityTypeDetailed = contentEntityTypes.find(
    (c) => c.id === contentEntityType.id
  )!;

  const { fields, append, remove } = useFieldArray({
    control,
    name: `profiles.${profileIndex}.texts`,
  });

  return (
    <div>
      <div className={`md:col-span-${3} col-span-${3} `}>
        <div className=" space-y-2 ">
          {contentEntityType.narratorSelectable && (
            <div className="flex space-s-2 mt-2">
              <InputLabel text="Texts" name="texts" />
              {texts?.length === 0 && contentEntityType.narratorSelectable && (
                <Button
                  bg="ring-color"
                  shape={'circle'}
                  type={'text'}
                  color={'text-textPrimary'}
                  Icon={PlusIcon}
                  onClick={() => {
                    append({
                      isDefault: texts.length === 0 ? true : false,
                      text: '',
                    });
                  }}
                />
              )}
            </div>
          )}
          <div className="space-y-6">
            {texts?.map(
              (item, index) =>
                item.isDefault && (
                  <ContentEntityTexts
                    key={item.text}
                    index={index}
                    control={control}
                    narratorSelectable={contentEntityType.narratorSelectable}
                    profileIndex={profileIndex}
                    isRtl={isRtl}
                    contentEntityType={contentEntityTypeDetailed?.name}
                  />
                )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProfileNarratorAudiosFieldsProps<FormFields extends FieldValues>
  extends UseControllerProps<FormFields> {
  profileIndex: number;
  contentEntityType: Server.ContentEntityTypeDetailedOutput;
  narratorAudios: Server.NarratorAudiosInput[] | undefined;
  setValue: UseFormSetValue<Server.CreateContentEntityByTypeIdV2Input>;
  register: UseFormRegister<Server.CreateContentEntityByTypeIdV2Input>;
}

export const ProfileNarratorAudiosFields = ({
  control,
  profileIndex,
  contentEntityType,
  narratorAudios,
  setValue,
  register,
}: ProfileNarratorAudiosFieldsProps<Server.CreateContentEntityByTypeIdV2Input>) => {
  const narrators = useContentSelector((s) => s.narrators.lookups ?? []);

  const defaultNarratorId = 0;

  const { fields, append, remove } = useFieldArray({
    control,
    name: `profiles.${profileIndex}.narratorAudios`,
  });

  return (
    <div className={`md:col-span-${3} col-span-${3} `}>
      <div className="">
        {contentEntityType.narratorSelectable && (
          <div className="flex space-s-2">
            {<InputLabel required={true} text="Audios" name="audios" />}
            {
              <Button
                bg="ring-color"
                shape={'circle'}
                type={'text'}
                color="text-textPrimary"
                Icon={PlusIcon}
                onClick={() => {
                  append({
                    isDefault: narratorAudios?.length === 0 ? true : false,
                    narratorId: defaultNarratorId,
                  });
                }}
              />
            }
          </div>
        )}
        <div className="space-y-6">
          {narratorAudios?.map((item, index) => (
            <ContentEntityNarratorAudio
              register={register}
              index={index}
              {...register(`profiles.${profileIndex}.narratorAudios`)}
              key={item.narratorId}
              narratorAudios={narratorAudios}
              narrators={narrators}
              removeNarratorAudio={remove}
              setValue={setValue}
              control={control}
              narratorSelectable={contentEntityType.narratorSelectable == true}
              profileIndex={profileIndex}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
